import { useEffect, useMemo, useRef } from "react";

import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import {
  formatCriadoEm,
  formatCriadoEmLista,
  rotuloStatusFel,
  FICHAS_NO_VALUE,
} from "@/screens/financeiro/obras/fichas/ficha-formatacao/ficha-formatacao";
import {
  FICHAS_FORBIDDEN_MESSAGE,
  useFichasFinanceiras,
  type FichasQueryStatus,
} from "../../hooks/use-fichas-financeiras";
import { ABRIR_FICHA_LABEL } from "./fichas-tabela.constants";
import type { FichaLinha, FichasTabelaProps } from "./types-fichas-tabela";

interface DerivarEstadoEntrada {
  oculta: boolean;
  status: FichasQueryStatus;
  temFichas: boolean;
}

/** Estado da tela: com linhas, a tabela segue na tela mesmo enquanto uma nova consulta carrega. */
export function derivarEstado({ oculta, status, temFichas }: DerivarEstadoEntrada) {
  if (oculta) return "oculta" as const;
  if (status === "forbidden") return "proibido" as const;
  if (status === "error") return "erro" as const;
  if (temFichas) return "tabela" as const;
  return status === "loading" ? ("carregando" as const) : ("vazio" as const);
}

/**
 * Tabela das fichas (HU 2679): consulta no Model, estado da tela (carregando, sem permissão, erro,
 * vazio ou tabela) e linhas prontas. A consulta fica viva com a tabela oculta para que Cancelar não
 * refaça a busca e salvar uma ficha atualize a lista por trás da modal.
 */
export function useFichasTabela({
  obraId,
  oculta,
  recarregarEm,
  onAbrir,
  onGerar,
}: FichasTabelaProps) {
  const { status: permissoesStatus, hasPermissao } = usePermissoes();
  // Sabendo que falta a 020, nem consulta: mostra "Acesso não autorizado".
  const semPermissaoConsulta =
    permissoesStatus === "success" && !hasPermissao(HUB_PERMISSOES.FINANCEIRO_FICHAS_CONSULTA);
  const fichas = useFichasFinanceiras(obraId, !semPermissaoConsulta);
  const { reload } = fichas;

  const recarregarAnterior = useRef(recarregarEm);
  useEffect(() => {
    if (recarregarAnterior.current === recarregarEm) return;
    recarregarAnterior.current = recarregarEm;
    reload();
  }, [recarregarEm, reload]);

  const linhas = useMemo<FichaLinha[]>(
    () =>
      fichas.items.map((ficha) => ({
        id: ficha.id,
        criadoEm: formatCriadoEmLista(ficha.criadoEm),
        statusFel: rotuloStatusFel(ficha.statusFel),
        solicitante: ficha.solicitante || FICHAS_NO_VALUE,
        rotuloAbrir: `${ABRIR_FICHA_LABEL} de ${formatCriadoEm(ficha.criadoEm)}`,
        statusTestId: `ficha-status-${ficha.id}`,
        abrirTestId: `ficha-open-${ficha.id}`,
      })),
    [fichas.items],
  );

  const status = semPermissaoConsulta ? ("forbidden" as const) : fichas.status;
  const estado = derivarEstado({ oculta, status, temFichas: linhas.length > 0 });

  return {
    estado,
    errorMessage: semPermissaoConsulta ? FICHAS_FORBIDDEN_MESSAGE : fichas.errorMessage,
    recarregar: reload,
    podeGerarFicha: hasPermissao(HUB_PERMISSOES.FINANCEIRO_FICHAS_GERACAO),
    linhas,
    total: fichas.total,
    tabela: fichas.table,
    carregandoTabela: fichas.status === "loading",
    abrir: onAbrir,
    gerar: onGerar,
  };
}
